import React, { useEffect, useState } from "react";

import { FormContext } from "../forms";
import { OnFidoSDKCheckProps, SDKCheckStatus } from "./types";
import { Applicant } from "../onfido-sdk/types";
import {
    DocumentCheckIncomplete,
    DocumentChecking,
    DocumentDetailsFailure,
    DocumentImagePoorQuality,
    DocumentNotSupported,
    DocumentVerified,
    ReloadingJourney
} from "./screens";
import { getDocDetails, initNewWorkflow } from "./services";

const getApplicant = (
    initiator: string,
    context?: FormContext
) => {
    if (context) {
        const onFidoSDKControl = context.getControl(initiator);
        const onFidoSDKValue = context.getValue(initiator);

        if (onFidoSDKControl && onFidoSDKControl.inputs) {
            return {
                sdkToken: onFidoSDKValue.sdkToken,
                attemptCount: onFidoSDKValue.attemptCount,
                jumpToPage: onFidoSDKValue.jumpToPage,
                applicantId: onFidoSDKValue[onFidoSDKControl.inputs[0].id],
                processId: onFidoSDKValue[onFidoSDKControl.inputs[1].id],
                workflowRunId: onFidoSDKValue[onFidoSDKControl.inputs[2].id],
                selectedDocument: onFidoSDKValue[onFidoSDKControl.inputs[3].id],
            };
        } else {
            return {};
        }
    } else {
        return {};
    }
}

const skipSDKCheck = (
    initiator: string,
    context: FormContext
) => {
    const onFidoSDKValue = context.getValue(initiator);
    
    // Reset jumpToPage
    context.setValue(initiator, {
        ...onFidoSDKValue,
        'jumpToPage': undefined
    });
    context.gotoNextPage();
};

const initSDKCheck = async (
    applicant: Applicant,
    setStatus: (status: SDKCheckStatus) => void
) => {
    // Set 10 sec timeout if it's not the first attempt
    if (applicant.attemptCount && applicant.attemptCount > 1) {
        await new Promise(resolve => setTimeout(resolve, 10000)).then();
    }

    const tempStatus = await getDocDetails(applicant);
    setStatus(tempStatus);
};

const resetJourney = async (
    applicant: Applicant,
    initiator: string,
    setStatus: (status: SDKCheckStatus) => void,
    context?: FormContext,
    name?: string
) => {
    if (context) {
        setStatus("reload-in-progress");
        const updatedApplicant = await initNewWorkflow(applicant);
        const onFidoSDKControl = context.getControl(initiator);
        const onFidoSDKValue = context.getValue(initiator);

        if (onFidoSDKControl && onFidoSDKControl.inputs) {
            name && context.unsetValue(name);
            if (updatedApplicant.workflowRunId) {
                context.setValue(initiator, {
                    ...onFidoSDKValue,
                    'sdkToken': updatedApplicant.sdkToken,
                    'attemptCount': onFidoSDKValue.attemptCount + 1,
                    'jumpToPage': context.state.page,
                    [onFidoSDKControl.inputs[2].id]: updatedApplicant.workflowRunId,
                    [onFidoSDKControl.inputs[3].id]: "", // Unset selected document
                });
            } else {
                context.setValue(initiator, {
                    'jumpToPage': context.state.page
                });
            }
            context.gotoPage(onFidoSDKControl.pageNumber);
        }
    }
}

const OnFidoSDKCheck = ({
    name,
    initiator,
    context,
    onChange
}: OnFidoSDKCheckProps) => {
    const [status, setStatus] = useState<SDKCheckStatus>("check-in-progress");
    const applicant: Applicant = getApplicant(initiator, context);

    const triggerResetJourney = async () => {
        await resetJourney(applicant, initiator, setStatus, context, name);
    };

    const verifyJourney = () => {
        onChange && onChange("verified");
    };

    useEffect(() => {
        if (context && applicant.jumpToPage) {
            skipSDKCheck(initiator, context);
        } else {
            initSDKCheck(applicant, setStatus);
        }
    }, []);

    switch (status) {
        case "check-incomplete":
            return <DocumentCheckIncomplete context={context} />;
        case "doc-not-supported":
            return <DocumentNotSupported resetJourney={triggerResetJourney}/>;
        case "doc-image-poor-quality":
            return <DocumentImagePoorQuality resetJourney={triggerResetJourney} />;
        case "doc-details-failure":
            return <DocumentDetailsFailure resetJourney={triggerResetJourney} />;
        case "doc-verified":
            return <DocumentVerified context={context} verifyJourney={verifyJourney} />;
        case "reload-in-progress":
            return <ReloadingJourney />;
        case "check-in-progress":
        default:
            return <DocumentChecking />;
    }
}

export default OnFidoSDKCheck;
